import React, { Suspense, useMemo } from "react";

import { Loader } from "@braintrust/braintrust-ui-components";
import { CloseIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useUser } from "@js/apps/common/hooks";
import { Wizard } from "@js/components/wizard";
import type { CrumbProps } from "@js/components/wizard/breadcrumbs";
import { hasPermission } from "@js/utils";

import { useEmployerId } from "../hooks";
import { useCanSetupATSIntegration } from "../hooks/use-can-setup-ats-integration";

type EditWizardProps = {
  pageTitle: string;
  continueButtonColor?: string;
  onContinue?: () => void;
  continueButtonText?: string;
  className?: string;
  children: React.ReactNode;
  disableSubmit?: boolean;
};

export const EmployerEditWizard = ({
  continueButtonText,
  children,
  ...props
}: EditWizardProps) => {
  const employerId = useEmployerId();
  const backUrl = `/employers/${employerId}/`;
  const user = useUser();
  const { canSetupATSIntegration } = useCanSetupATSIntegration();

  const wizardItems: CrumbProps[] = useMemo(() => {
    const items = [
      {
        title: "Personal Info",
        path: "/employer/edit/personal_info/",
      },
    ];
    if (
      !hasPermission(user, ENUMS.CustomPermission.CAN_MANAGE_EMPLOYER_ACCOUNT)
    ) {
      return items;
    }

    items.push(
      {
        title: "Organization Info",
        path: "/employer/edit/organization_info/",
      },
      {
        title: "Team Members",
        path: "/employer/edit/team_members/",
      },
    );

    if (canSetupATSIntegration) {
      items.push({
        title: "ATS integration",
        path: "/employer/edit/ats_integration/",
      });
    }

    return items;
  }, [user, canSetupATSIntegration]);

  return (
    <Wizard
      crumbs={wizardItems}
      customBackURL={backUrl}
      customBackIcon={CloseIcon}
      oneActive
      hideSeparator
      continueButtonText={continueButtonText || "Save"}
      {...props}
    >
      <Suspense fallback={<Loader centered />}>{children}</Suspense>
    </Wizard>
  );
};
